import type { LanguageText } from "@/types/config";

import {
  hasDefaultCountryLocale,
  isDefaultCountryLocale,
} from "@/mixins/translatedProperty";

/**
 * Merge a (possibly partial) WebSocket event payload into a cached object,
 * IN PLACE (docs/tasks/ws-event-handling-rework.md §4.3):
 * - plain objects: deep merge — keys missing from the patch are preserved,
 *   so partial payloads cannot wipe cached data
 * - arrays: REPLACED wholesale — update events always carry complete arrays
 *   (core guarantee, task doc OQ-1); index-wise merging of reordered or
 *   shrunk lists is never correct
 * - primitives: replaced
 *
 * Mutating in place is deliberate: Vue 3 proxies track nested mutation, so
 * no clone is needed and object identity stays stable for watchers.
 */
export function mergeEventPayload<T extends object>(
  target: T,
  patch: Partial<T>
): T {
  for (const key of Object.keys(patch) as (keyof T)[]) {
    const patchValue = patch[key];
    const targetValue = target[key];
    if (Array.isArray(patchValue)) {
      target[key] = patchValue as T[keyof T];
    } else if (
      patchValue !== null &&
      typeof patchValue === "object" &&
      targetValue !== null &&
      typeof targetValue === "object" &&
      !Array.isArray(targetValue)
    ) {
      mergeEventPayload(targetValue as object, patchValue as Partial<object>);
    } else {
      target[key] = patchValue as T[keyof T];
    }
  }
  return target;
}

export function useDataHelper() {
  function objectsDeepEqual(obj1: any, obj2: any) {
    if (obj1 === obj2) {
      return true;
    }

    if (
      obj1 == null ||
      typeof obj1 !== "object" ||
      obj2 == null ||
      typeof obj2 !== "object"
    ) {
      return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (!keys2.includes(key) || !objectsDeepEqual(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  }

  function updateObjectByKeys(target: any, updates: any) {
    for (const key in updates) {
      if (target && updates.hasOwnProperty(key)) {
        target[key] = updates[key];
      }
    }
    return target;
  }

  function updateExistingObjectKeys(target: any, updates: any, add = false) {
    if (target == null || updates == null) {
      return target;
    }

    for (const key in updates) {
      if (updates.hasOwnProperty(key)) {
        const targetHasKey = target.hasOwnProperty(key);
        const updateVal = updates[key];
        const targetVal = target[key];

        if (targetHasKey) {
          if (
            typeof targetVal === "object" &&
            typeof updateVal === "object" &&
            targetVal !== null &&
            updateVal !== null
          ) {
            updateExistingObjectKeys(targetVal, updateVal, add);
          } else {
            target[key] = updateVal;
          }
        } else if (add) {
          target[key] = deepCopy(updateVal);
        }
      }
    }
    return target;
  }

  function deepCopy(obj: any): any {
    if (obj === null || typeof obj !== "object") {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => deepCopy(item));
    }

    const copy: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        copy[key] = deepCopy(obj[key]);
      }
    }
    return copy;
  }

  function standardizeLangTexts(
    obj: LanguageText,
    langCode?: string
  ): LanguageText {
    if (!obj) return obj;
    const result: LanguageText = { ...obj };

    if (langCode && langCode.includes("_")) {
      const shortLang = langCode.split("_")[0];

      if (hasDefaultCountryLocale(langCode)) {
        if (isDefaultCountryLocale(langCode) && result[shortLang]) {
          delete result[shortLang];
        }
      } else if (shortLang && result[shortLang]) {
        delete result[shortLang];
      }
    }

    for (const key in result) {
      if (result[key] === "") {
        delete result[key];
      }
    }

    return result;
  }

  function isNonEmptyObject(obj: unknown): obj is Record<string, unknown> {
    return (
      typeof obj === "object" &&
      obj !== null &&
      !Array.isArray(obj) &&
      Object.keys(obj).length > 0
    );
  }

  return {
    objectsDeepEqual,
    updateObjectByKeys,
    updateExistingObjectKeys,
    standardizeLangTexts,
    isNonEmptyObject,
  };
}
